import { useEffect, useState } from 'react';
import { apiRequest } from '../api/client.js';

export interface TenantIdentity {
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly companyCode: string | null;
  readonly companyName: string | null;
  readonly applicationName: string | null;
  readonly logoDataUrl: string | null;
  /** Platform-tier actions are offered only to a platform administrator. */
  readonly isPlatformAdmin: boolean;
  /**
   * How this tenant writes a business day — the DISPLAY_DATE_FORMAT system
   * parameter, resolved server-side for the signed-in tenant. Carried here
   * because every screen renders dates while only an administrator may read
   * /v1/config, and because this request is already made once per session.
   */
  readonly dateFormat: string;
  /**
   * How this tenant groups amounts and quantities — the DISPLAY_NUMBER_FORMAT
   * system parameter, resolved server-side. Carried on the same read as
   * `dateFormat` for the same reason: one question, one answer, one request.
   */
  readonly numberFormat: string;
}

// The header renders on every screen, so without a shared cache each mount
// would issue its own request — the duplicate-fetch problem <Can>/useMenu hit.
let inFlight: { key: string; promise: Promise<TenantIdentity | null> } | null = null;
const subscribers = new Set<() => void>();

export function clearTenantIdentityCache(): void {
  inFlight = null;
}

/** Re-read mounted consumers after a successful tenant-configuration change. */
export function refreshTenantIdentity(): void {
  clearTenantIdentityCache();
  for (const refresh of subscribers) refresh();
}

function load(token: string): Promise<TenantIdentity | null> {
  if (inFlight && inFlight.key === token) return inFlight.promise;
  const promise = apiRequest<{ data: TenantIdentity | null }>('/v1/me/tenant', { token })
    .then((r) => r.data)
    .catch((e: unknown) => {
      // Never cache a failure, or a transient error would blank the header
      // for the rest of the session. The failure is passed on, not turned
      // into "no tenant": a screen that builds identifiers from the tenant
      // must be able to tell the two apart.
      if (inFlight?.promise === promise) inFlight = null;
      throw e;
    });
  inFlight = { key: token, promise };
  return promise;
}

export interface TenantIdentityState {
  /** Null while unknown — still loading, or the read failed. */
  readonly identity: TenantIdentity | null;
  /** Why the identity could not be read; null when it was read or is still loading. */
  readonly error: string | null;
}

/** The tenant/company the signed-in user is working in, with the reason it is unknown. */
export function useTenantIdentityState(token: string | null): TenantIdentityState {
  const [state, setState] = useState<TenantIdentityState>({ identity: null, error: null });

  useEffect(() => {
    if (!token) {
      setState({ identity: null, error: null });
      return;
    }
    let alive = true;
    const refresh = (): void => {
      load(token).then(
        (v) => { if (alive) setState({ identity: v, error: null }); },
        (e: unknown) => { if (alive) setState({ identity: null, error: (e as Error).message }); },
      );
    };
    subscribers.add(refresh);
    refresh();
    return () => {
      alive = false;
      subscribers.delete(refresh);
    };
  }, [token]);

  return state;
}

/** The tenant/company the signed-in user is working in. Null while unknown. */
export function useTenantIdentity(token: string | null): TenantIdentity | null {
  return useTenantIdentityState(token).identity;
}
